import {isKeyPressed, keyCodes} from '@ey/utils';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import React, {Fragment, useCallback, useEffect, useState, useContext} from 'react';
import styled from 'styled-components';
import {clientSideEventNames} from '../../../util/uiconstants';
import {isValidFunction, labels, numberFormattedText} from '../../../util/utils';
import FormField from '../BaseComponentWrappers/MotifFormField';
import Input from '../BaseComponentWrappers/MotifInput';
import Label from '../BaseComponentWrappers/MotifLabel';
import ProgressBar from '../BaseComponentWrappers/MotifProgressBar';
import CanvasFormInputButtons from './CanvasFormInputButtons';
import {getUnresolvedCommentsCount} from '../../../actions/ValidationControl/validationcontrolactions';
import {DocumentTypeIdContext} from '../../Main/Forms/CanvasFormContainer';
import {useDocumentId, useEngagementId, useUserPreferences} from '../../../util/customHooks';

// ********************* This is a common component. Only Arsenal can change this ********************* //
// ********************* This is a common component. Only Arsenal can change this ********************* //

export default function CanvasFormInput({
	autoFocus = false,
	children,
	className,
	clearButtonTitle,
	disabled,
	hideClearButton,
	id,
	isReadOnly,
	label,
	showLabel,
	maxLength,
	minLength,
	onAccept,
	onBlur,
	onCancel,
	onChange,
	onChanged,
	onEdit,
	onFocus,
	pattern,
	placeholder,
	progressBarClassName,
	progressBarLabel,
	required,
	showProgressBar,
	type,
	value,
	saveInput = true,
	hasChanges,
	customCallback,
	isEditingOn,
	showPlainText,
	isAdjustedBasis
}) {
	const [isEditing, setIsEditing] = useState(false),
		[isSaving, setIsSaving] = useState(false),
		[bodyFreeText, setBodyFreeText] = useState(value);

	const dispatch = useDispatch();

	const isAresView = useSelector((state) => state.isAresView);

	const engagementId = useEngagementId(),
		documentId = useDocumentId(),
		documentTypeId = useContext(DocumentTypeIdContext),
		userPreferences = useUserPreferences();

	const inputNumericDataTypes = ['number', 'integer', 'percentage'];

	// Motif input does not show the value after formatting for numeric types
	const motifInputType = inputNumericDataTypes.indexOf(type) !== -1 ? 'text' : type;

	let inputDisplayValue = bodyFreeText;
	if (!isEditing && inputNumericDataTypes.indexOf(type) !== -1 && bodyFreeText) {
		inputDisplayValue = numberFormattedText(userPreferences.numberFormat, bodyFreeText);

		if (type === 'percentage') {
			inputDisplayValue += '%';
		}
	}

	const handleOnClick = useCallback(() => {
		if (!isReadOnly && !disabled) {
			setIsEditing(true);
			onEdit && onEdit(id);
			if (isEditingOn) {
				isEditingOn(true);
			}
		}
	}, [disabled]);

	const handleOnChange = useCallback(
		(event) => {
			const targetValue = event.target.value;
			if (saveInput) setBodyFreeText(targetValue);
			onChange && onChange(targetValue);
		},
		[onChange]
	);

	const handlerCustomCallback = () => {
		const removeLoading = ({saving = false, editing = false} = {saving: false, editing: false}) => {
			setIsSaving(saving);
			setIsEditing(editing);
		};

		const textValue = bodyFreeText;
		customCallback(textValue, removeLoading);
	};

	const handleOnAccept = useCallback((val, evt) => {
		setIsSaving(true);
		if (!customCallback) {
			if (isValidFunction(onAccept)) {
				const promise = onAccept(bodyFreeText);
				if (promise instanceof Promise) {
					promise
						.then(() => {
							if (isValidFunction(onChanged)) {
								onChanged(id, value);
								dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId));
							}
							dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId));
						})
						.finally(() => {
							setIsSaving(false);
							setIsEditing(false);
						});
				}
			}
		} else {
			handlerCustomCallback();
		}
	}, [bodyFreeText, onAccept, onChanged]);

	const handleOnCancel = useCallback((val, evt) => {
		evt.stopPropagation();
		setIsEditing(false);
		setBodyFreeText(value);
		onCancel && onCancel(id);
		if (isEditingOn) {
			isEditingOn(false);
		}
	}, [value, onCancel]);

	const handleOnKeyDown = useCallback(
		(event) => {
			if (isKeyPressed(event.nativeEvent)) {
				handleOnAccept();
			} else {
				if (isKeyPressed(event.nativeEvent, keyCodes.ESCAPE)) {
					handleOnCancel(event.target.value, event);
				}
			}
		},
		[handleOnAccept, handleOnCancel]
	);

	const handleGetValidations = useCallback(() => {
		setIsSaving(false);
	}, []);

	useEffect(() => {
		window.addEventListener(clientSideEventNames.getValidations, handleGetValidations);
		return () => {
			window.removeEventListener(clientSideEventNames.getValidations, handleGetValidations);
		};
	}, [handleGetValidations]);

	useEffect(() => {
		if (!isAdjustedBasis && (value !== bodyFreeText || hasChanges)) {
			setBodyFreeText(value);
		}
	}, [value, hasChanges]);

	useEffect(() => {
		if (disabled && isEditing) {
			setIsEditing(false);
		}
	}, [disabled]);

	return (
		<StyledCanvasFormInput
			className={`StyledCanvasFormInput ${isAresView && 'StyledCanvasFormInputAres'} ${
				parseInt(bodyFreeText) >= 0 ? 'styledCanvasFormInputValue' : ''
			} ${className}`}
			isEditing={isEditing}
			isReadOnly={isReadOnly}
		>
			{isSaving && showProgressBar && (
				<ProgressBar
					className={`greyOutArea ${progressBarClassName}`}
					labelComponent={<span>{progressBarLabel}</span>}
				/>
			)}
			<FormField onClick={handleOnClick}>
				<Fragment>
					{(showLabel || !bodyFreeText) && (
						<Label position="in" title={label}>
							{label}
						</Label>
					)}
					<Input
						autoFocus={autoFocus}
						clearButtonTitle={clearButtonTitle}
						disabled={disabled || (isReadOnly && !isEditing)}
						hideClearButton={hideClearButton}
						id={id}
						maxLength={maxLength}
						minLength={minLength}
						onBlur={onBlur}
						onChange={handleOnChange}
						onFocus={onFocus}
						onKeyDown={handleOnKeyDown}
						pattern={pattern}
						placeholder={placeholder}
						required={required}
						type={motifInputType}
						value={inputDisplayValue}
						onClick={handleOnClick}
						suffix={
							isEditing && <CanvasFormInputButtons value={bodyFreeText} onAccept={handleOnAccept} onCancel={handleOnCancel} />
						}
					/>
				</Fragment>
			</FormField>
		</StyledCanvasFormInput>
	);
}

CanvasFormInput.propTypes = {
	/* Auto focus the input component when it renders. */
	autoFocus: PropTypes.bool,
	/* Class Name will be append to the FormField component. */
	className: PropTypes.string,
	/* Title for the clear button */
	clearButtonTitle: PropTypes.string,
	/* Whether the component is disabled or not. */
	disabled: PropTypes.bool,
	/* Whether the clear button is hidden or not. */
	hideClearButton: PropTypes.bool,
	/* Identifier for the input component. */
	id: PropTypes.string.isRequired,
	/* Whether the input allows editable text. */
	isReadOnly: PropTypes.bool,
	/* Label text for the component. */
	label: PropTypes.string,
	/* Maximum value length. */
	maxLength: PropTypes.number,
	/* Minimum value length. */
	minLength: PropTypes.number,
	/* Event handler for onAccept (click accept button). */
	onAccept: PropTypes.func.isRequired,
	/* Event handler for onBlur. */
	onBlur: PropTypes.func,
	/* Event handler for onCancel (click cancel button). */
	onCancel: PropTypes.func.isRequired,
	/* Event handler for onChange. */
	onChange: PropTypes.func,
	/* Event handler for promise completion. */
	onChanged: PropTypes.func,
	/* Event handler for editing. */
	onEdit: PropTypes.func,
	/* Event handler for onFocus. */
	onFocus: PropTypes.func,
	/* Pattern for the input component. */
	pattern: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
	/* Placeholder for the input component. */
	placeholder: PropTypes.string,
	/* ClassName for the progress bar component. */
	progressBarClassName: PropTypes.string,
	/* ClassName for the label component. */
	progressBarLabel: PropTypes.string,
	/* Whether the input is required or not. */
	required: PropTypes.bool,
	/* If true, the progress bar will be shown. */
	showLabel: PropTypes.bool,
	/* If true, the progress bar will be shown. */
	showProgressBar: PropTypes.bool,
	/* Type for the input value. */
	type: PropTypes.oneOf(['date', 'email', 'number', 'password', 'tel', 'text']),
	/* Input value. */
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

CanvasFormInput.defaultProps = {
	autoFocus: false,
	className: 'canvas-form-input',
	disabled: false,
	hideClearButton: true,
	isReadOnly: false,
	progressBarClassName: 'canvas-form-input-progress-bar',
	progressBarLabel: labels.saving,
	showLabel: false,
	showProgressBar: true,
	type: 'text',
	hasChanges: false
};

const StyledCanvasFormInput = styled.section`
	display: flex;
	flex-direction: row;
	position: relative;
	.motif-progress-bar-wrapper {
		position: absolute !important;
		left: 0;
		top: 0;
		width: 100% !important;
		height: 100% !important;
	}

	.StyledCanvasFormInputDisplay {
		width: 100%;
	}

	.motif-form-field {
		width: calc(100% - var(--px-80));
	}
	&.StyledCanvasFormInputAres {
		width: var(--px-400) !important;
		height: var(--px-44) !important;
		margin-right: var(--px-16);
		.motif-form-field {
			height: 100% !important;
			width: 100% !important;
			.motif-progress-bar-wrapper {
				display: flex;
				align-items: center;
				width: 100%;
				height: 100%;
			}
			.StyledCanvasFormInputDisplay {
				border: 0;
				background: var(--neutrals-00white);
				height: var(--px-52);
				border-radius: var(--px-8);
				.canvas-form-input-display-value {
					height: 100%;
					padding-left: var(--px-6);
					padding-top: var(--px-22);
					line-height: var(--px-22);
					color: var(--neutrals-900);
					font-size: var(--px-14);
					font-weight: 300;
				}
			}
			.motif-input-component {
				.motif-input-right-components {
					height: var(--px-45);
				}
			}
		}
		.StyledConfirmInputIcons {
			height: 100%;
			width: var(--px-60);
			.save-btn,
			.cancel-btn {
				margin: var(--px-15) 0;
				width: var(--px-24);
				margin-right: var(--px-10) !important;
				.motif-icon {
					svg {
						fill: var(--blue-600);
						color: var(--blue-600);
					}
				}
			}
		}
	}
`;
